import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactFlow, { Background, Controls, MarkerType } from 'reactflow';
import 'reactflow/dist/style.css';
import { Play, Loader2, RefreshCw, AlertTriangle, Download, Package, ShieldCheck, Award } from 'lucide-react';
import { fetchEtlClientesStatus, triggerEtlClientesRun } from '../../api';

// Color representativo por etapa (identidad visual), independiente del estado de la corrida
// bronce/plata/oro usan tonos metalicos fieles a las medallas reales
const STAGES = [
  { key: 'extraccion', label: 'Extracción', sub: 'Google Sheets', icon: Download, color: '#00B8FF', bg: '#00212e' },
  { key: 'bronce', label: 'Bronce', sub: 'Datos raw', icon: Package, color: '#CD7F32', bg: '#2a1a0d' },
  { key: 'silver', label: 'Silver', sub: 'Validaciones', icon: ShieldCheck, color: '#C0C0C0', bg: '#22262b' },
  { key: 'gold', label: 'Gold', sub: 'Datos de negocio', icon: Award, color: '#D4AF37', bg: '#2e2408' },
  { key: 'quarantine', label: 'Cuarentena', sub: 'Registros rechazados', icon: AlertTriangle, color: '#ff5c7a', bg: '#2a0d14' },
];

// Estado del proceso manda en el recuadro (blanco = pendiente, azul = en curso, verde = OK, rojo = error)
// el color de la etapa (bronce/plata/oro/etc.) se conserva solo en el icono, como identidad
function stageVisualState(stage, state) {
  const status = state?.status || 'pending';
  if (status === 'success') {
    return {
      border: '#00FF66',
      bg: '#0d2318',
      glow: '0 0 0 3px rgba(0,255,102,0.2), 0 0 16px rgba(0,255,102,0.35)',
      className: '',
      badge: '✓',
      badgeColor: '#00FF66',
    };
  }
  if (status === 'error') {
    return {
      border: '#ff5c7a',
      bg: '#2a0d14',
      glow: '0 0 0 3px rgba(255,92,122,0.2), 0 0 16px rgba(255,92,122,0.4)',
      className: '',
      badge: '✕',
      badgeColor: '#ff5c7a',
    };
  }
  if (status === 'running') {
    return {
      border: '#00B8FF',
      bg: '#04202e',
      glow: '0 0 0 3px rgba(0,184,255,0.25), 0 0 16px rgba(0,184,255,0.5)',
      className: 'etl-node-running',
      badge: null,
      badgeColor: '#00B8FF',
    };
  }
  // pendiente: vista estática, sin animar
  return { border: '#e5e7eb55', bg: '#12181f', glow: 'none', className: '', badge: null, badgeColor: '#e5e7eb' };
}

function buildNodes(stageStates) {
  const gap = 260;
  return STAGES.map((stage, idx) => {
    const state = stageStates?.[stage.key] || { status: 'pending', detail: null };
    const visual = stageVisualState(stage, state);
    const Icon = stage.icon;
    return {
      id: stage.key,
      position: { x: idx * gap, y: 0 },
      className: visual.className,
      data: {
        label: (
          <div style={{ textAlign: 'left', minWidth: 170 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <Icon size={14} color={stage.color} />
              <span style={{ fontWeight: 700, fontSize: 13, color: '#fff' }}>{stage.label}</span>
              {visual.badge && (
                <span style={{ marginLeft: 'auto', color: visual.badgeColor, fontSize: 12, fontWeight: 700 }}>{visual.badge}</span>
              )}
            </div>
            <div style={{ fontSize: 10.5, opacity: 0.75, marginTop: 2, color: '#cfd8d4' }}>{stage.sub}</div>
            {state.detail && (
              <div style={{ fontSize: 10, marginTop: 4, color: visual.badgeColor, opacity: 0.95 }}>
                {state.detail.length > 60 ? `${state.detail.slice(0, 60)}…` : state.detail}
              </div>
            )}
          </div>
        ),
      },
      style: {
        background: visual.bg,
        border: `2px solid ${visual.border}`,
        borderRadius: 10,
        padding: '10px 14px',
        minWidth: 190,
        boxShadow: visual.glow,
        transition: 'background .3s ease, border-color .3s ease, box-shadow .3s ease',
      },
      sourcePosition: 'right',
      targetPosition: 'left',
    };
  });
}

function buildEdges(stageStates) {
  const edges = [];
  for (let i = 0; i < STAGES.length - 1; i++) {
    const from = STAGES[i].key;
    const to = STAGES[i + 1].key;
    const fromStatus = stageStates?.[from]?.status || 'pending';
    const toStatus = stageStates?.[to]?.status || 'pending';
    const isActive = fromStatus === 'success' && toStatus === 'running';
    const isDone = fromStatus === 'success' && (toStatus === 'success' || toStatus === 'error');
    const color = isActive ? '#00B8FF' : isDone ? '#00FF66' : '#3a4048';
    edges.push({
      id: `${from}-${to}`,
      source: from,
      target: to,
      // Solo el tramo que se está recorriendo en este momento se anima; lo ya completado queda sólido
      animated: isActive,
      markerEnd: { type: MarkerType.ArrowClosed, color },
      style: { stroke: color, strokeWidth: isActive ? 3 : 2 },
    });
  }
  return edges;
}

function formatTime(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('es-MX', { hour: '2-digit', minute: '2-digit', second: '2-digit', day: '2-digit', month: 'short' });
  } catch {
    return iso;
  }
}

export default function EtlClientesFlow() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [triggering, setTriggering] = useState(false);
  const [error, setError] = useState(null);
  const pollRef = useRef(null);

  const loadStatus = useCallback(async () => {
    try {
      const data = await fetchEtlClientesStatus();
      setStatus(data);
      setError(null);
    } catch (err) {
      const detail = err?.response?.data?.detail || 'No se pudo obtener el estado del ETL de clientes.';
      setError(detail);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadStatus();
    return () => clearInterval(pollRef.current);
  }, [loadStatus]);

  useEffect(() => {
    clearInterval(pollRef.current);
    if (status?.overall_status === 'running') {
      pollRef.current = setInterval(loadStatus, 3000);
    }
    return () => clearInterval(pollRef.current);
  }, [status?.overall_status, loadStatus]);

  const handleRun = async () => {
    setTriggering(true);
    setError(null);
    try {
      await triggerEtlClientesRun();
      await loadStatus();
    } catch (err) {
      const detail = err?.response?.data?.detail || 'No se pudo iniciar la ejecución manual del ETL.';
      setError(detail);
    } finally {
      setTriggering(false);
    }
  };

  const nodes = useMemo(() => buildNodes(status?.stages), [status]);
  const edges = useMemo(() => buildEdges(status?.stages), [status]);

  const isRunning = status?.overall_status === 'running';

  return (
    <div className="p-6 space-y-6">
      {/* Panel de control */}
      <div className="bg-[#00261A] border border-[#00FF66]/25 rounded-xl p-6 shadow-lg flex flex-wrap items-center justify-between gap-4">
        <div>
          <h3 className="text-lg font-semibold text-[#00FF66] flex items-center gap-2">
            <Play className="w-5 h-5" />
            Ejecución del ETL de Clientes
          </h3>
          <p className="text-sm text-[#9fe8b9] mt-1">
            Flujo: Extracción → Bronce → Silver → Gold → Cuarentena. Corre automáticamente en segundo plano; aquí puedes disparar una corrida manual y ver en qué etapa falla.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={loadStatus}
            className="flex items-center gap-2 px-3 py-2 border border-[#00FF66]/30 text-[#9fe8b9] hover:border-[#00FF66]/55 hover:text-white rounded-lg [transition:all_.2s_ease]"
          >
            <RefreshCw className="w-4 h-4" />
            Refrescar
          </button>
          <button
            onClick={handleRun}
            disabled={triggering || isRunning}
            className="flex items-center gap-2 px-4 py-2 bg-[#00FF66]/15 border border-[#00FF66]/40 text-[#00FF66] hover:bg-[#00FF66]/25 disabled:opacity-40 disabled:cursor-not-allowed rounded-lg [transition:all_.2s_ease]"
          >
            {isRunning || triggering ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
            {isRunning ? 'Ejecutando…' : 'Ejecutar Manualmente'}
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-[#3a1f00] border border-[#ff9b00]/40 rounded-xl p-4 flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-[#ff9b00] mt-0.5 flex-shrink-0" />
          <p className="text-[#ffd9a0] text-sm">{error}</p>
        </div>
      )}

      {/* Info de la corrida actual/última */}
      {status?.run_id && (
        <div className="bg-[#00261A] border border-[#00FF66]/15 rounded-xl p-4 grid grid-cols-2 md:grid-cols-5 gap-4 text-sm">
          <div>
            <p className="text-[#5fbd85] text-xs">Run ID</p>
            <p className="font-mono text-xs text-white truncate">{status.run_id}</p>
          </div>
          <div>
            <p className="text-[#5fbd85] text-xs">Disparado por</p>
            <p className="text-white">{status.trigger === 'manual' ? 'Manual' : 'Programado'}</p>
          </div>
          <div>
            <p className="text-[#5fbd85] text-xs">Estado</p>
            <p className={
              status.overall_status === 'error' ? 'text-[#ff5c7a]' :
              status.overall_status === 'running' ? 'text-[#00B8FF]' : 'text-[#00FF66]'
            }>
              {status.overall_status}
            </p>
          </div>
          <div>
            <p className="text-[#5fbd85] text-xs">Inicio</p>
            <p className="text-white">{formatTime(status.started_at)}</p>
          </div>
          <div>
            <p className="text-[#5fbd85] text-xs">Fin</p>
            <p className="text-white">{formatTime(status.finished_at)}</p>
          </div>
          {status.error && (
            <div className="col-span-2 md:col-span-5">
              <p className="text-[#5fbd85] text-xs">Error</p>
              <p className="text-[#ff5c7a] text-xs break-all">{status.error}</p>
            </div>
          )}
        </div>
      )}

      {/* Diagrama de flujo */}
      <div className="bg-[#00170f] border border-[#00FF66]/20 rounded-xl shadow-lg" style={{ height: 340 }}>
        <style>{`
          /* Solo la etapa que est\u00e1 corriendo pulsa; en reposo/terminado la vista es est\u00e1tica */
          @keyframes etlPulseBlue {
            0%, 100% { box-shadow: 0 0 0 3px rgba(0,184,255,0.25), 0 0 14px rgba(0,184,255,0.45); }
            50% { box-shadow: 0 0 0 6px rgba(0,184,255,0.4), 0 0 26px rgba(0,184,255,0.85); }
          }
          .etl-node-running { animation: etlPulseBlue 1.2s ease-in-out infinite; }
          .react-flow__edge.animated .react-flow__edge-path { stroke-dasharray: 6; animation: dashdraw 0.5s linear infinite; }
        `}</style>
        {loading ? (
          <div className="flex items-center justify-center h-full text-[#9fe8b9]">
            <Loader2 className="w-6 h-6 animate-spin mr-2" /> Cargando flujo...
          </div>
        ) : (
          <ReactFlow
            nodes={nodes}
            edges={edges}
            fitView
            fitViewOptions={{ padding: 0.3 }}
            proOptions={{ hideAttribution: true }}
            nodesDraggable={false}
            nodesConnectable={false}
            zoomOnScroll={false}
          >
            <Background color="#0f3f2b" gap={20} />
            <Controls showInteractive={false} />
          </ReactFlow>
        )}
      </div>
    </div>
  );
}
